// Research wand (backend app/field_research.py, migration 202610052300).
//
//   useFieldResearch(objectType, recordId)   the page's controller: the newest
//                                            and the newest-applied run per
//                                            field, start / poll / apply /
//                                            dismiss, and which card is open
//   ResearchField({ ctl, field, label, kind, value, onApply, legacy })
//                                            what sits after a field's value:
//                                            the ✨ badge when the value came
//                                            from research, then the wand
//
// The wand replaces the old per-field "synced from DealCloud" glyph. A run
// looks for ONE value with the record's notes, emails and meeting
// transcripts, the web (Firecrawl) and Apollo. The card shows the run live,
// then the finding, how sure it is, the reasoning, the sources and every
// step it took. "Use this" saves through the field's own onSave (the record
// PATCH, exactly like typing it) and stamps the run as applied, which is
// what the ✨ badge reads afterwards.
//
// State lives in a per-page store, not in the card or the panel: the detail
// pages declare their Row component inside render, so anything mounted in a
// Row remounts on every parent render, and a panel re-render on every poll
// would remount the whole column.
//
// The run drawer (?panel=research, ResearchRunBody, record-page redesign
// step 8) shows any run in full — a field's research, a legacy AI fill, the
// ownership check, the size tier, an industry suggestion — through one
// adapter per source; the anchored card stays for a quick Use this / Dismiss.

const RESEARCH_CONF = {
  high:   { label: 'High confidence',   hue: 'green' },
  medium: { label: 'Medium confidence', hue: 'amber' },
  low:    { label: 'Low confidence',    hue: 'slate' },
};

const researchSame = (kind, a, b) => {
  if (a == null || b == null || a === '' || b === '') return false;
  if (kind === 'money') {
    const fa = Number(a), fb = Number(b);
    return Number.isFinite(fa) && Number.isFinite(fb) && fb !== 0 && Math.abs(fa - fb) / Math.abs(fb) < 0.005;
  }
  const n = (v) => String(v).trim().toLowerCase().replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');
  return n(a) === n(b);
};

// Atlas money columns treat 0 as "not filled" (money scale normalization);
// a long-text field holding "." or "NA" (1,161 placeholder descriptors on
// prod) is empty too.
const researchShow = (kind, v) => {
  if (v == null || v === '' || (kind === 'money' && Number(v) === 0)) return null;
  if (kind === 'longtext' && String(v).trim().length < 25) return null;
  return kind === 'money' ? fmtMoney(Number(v)) : String(v);
};
const researchClip = (t, n = 120) => (t && t.length > n ? t.slice(0, n - 1) + '…' : t);

// The triangulated revenue estimate (app/revenue_estimate) — the company
// Revenue wand and Prospecting show it the same way.
// $850K · $2.4M · $12M — two significant figures, no false precision.
const revMoney = (x) => {
  if (!x) return '—';
  for (const [div, unit] of [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']]) {
    if (x >= div) { const v = x / div; return `$${v < 10 ? String(Math.round(v * 10) / 10) : String(Math.round(v))}${unit}`; }
  }
  return `$${Math.round(x).toLocaleString()}`;
};
const revRange = (lo, hi) => {
  const a = revMoney(lo); const b = revMoney(hi);
  if (a === b) return a;
  return a.slice(-1) === b.slice(-1) && 'KMB'.includes(a.slice(-1)) ? `${a.slice(0, -1)}–${b.slice(1)}` : `${a}–${b}`;
};
// One number when the estimate is high confidence, the range otherwise.
const revLabel = (est) => (!est || est.status !== 'estimated' ? null : est.confidence === 'high' ? revMoney(est.base) : revRange(est.low, est.high));
const REV_WEIGHT_HUE = { high: 'green', medium: 'amber', low: 'slate' };
const REV_KIND_LABEL = { verified: 'stated', third_party: 'database', derived: 'derived', assumption: 'assumption' };
const revCap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
const revHost = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (_) { return u || ''; } };

// Low · Base · High and EBITDA, under the headline figure.
const RevenueRange = ({ est }) => (
  <>
    <div style={{ marginTop: 4, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>Low {revMoney(est.low)} · Base {revMoney(est.base)} · High {revMoney(est.high)}</div>
    {est.ebitda && est.ebitda_margin && (
      <div style={{ marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ color: 'var(--text-4)', marginRight: 6 }}>EBITDA</span>{revRange(est.ebitda.low, est.ebitda.high)}
        <span style={{ color: 'var(--text-4)' }}> · {est.ebitda_margin.low === est.ebitda_margin.high ? est.ebitda_margin.base : `${est.ebitda_margin.low}–${est.ebitda_margin.high}`}% margin</span>
      </div>
    )}
  </>
);

// Each method with its formula, its inputs (what kind of fact, where from, the quote), then the margin and what would tighten it.
const RevenueMethods = ({ est }) => (
  <>
    {(est.methods || []).length > 0 && (
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {est.methods.map((m, i) => (
          <div key={i} style={{ paddingLeft: 10, borderLeft: '2px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{m.label}</span>
              <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{revRange(m.low, m.high)}</span>
              <Pill hue={REV_WEIGHT_HUE[m.weight] || 'slate'} sz="sm">{revCap(m.weight)} weight</Pill>
            </div>
            {m.formula && <div style={{ marginTop: 2, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{m.formula}</div>}
            {(m.inputs || []).map((x, j) => (
              <div key={j} style={{ marginTop: 2, fontSize: 11.5, color: 'var(--text-3)', wordBreak: 'break-word' }}>
                {x.fact}<span style={{ color: 'var(--text-4)' }}> · {REV_KIND_LABEL[x.kind] || x.kind}</span>
                {x.source_url && <> · <a href={x.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{revHost(x.source_url)}</a></>}
                {x.quote && <div style={{ fontStyle: 'italic' }}>“{x.quote}”</div>}
              </div>
            ))}
            {m.note && <div style={{ marginTop: 2, color: 'var(--text-4)', fontSize: 11.5 }}>{m.note}</div>}
          </div>
        ))}
      </div>
    )}
    {est.ebitda_reasoning && <div style={{ marginTop: 8 }}><span style={{ color: 'var(--text-4)', marginRight: 6 }}>Margin</span>{est.ebitda_reasoning}</div>}
    {est.would_improve && <div style={{ marginTop: 6 }}><span style={{ color: 'var(--text-4)', marginRight: 6 }}>Would tighten it</span>{est.would_improve}</div>}
  </>
);

// A contact email run that found no stated address keeps the one the
// domain's own pattern gives (app/email_pattern): a guess with what it rests
// on. Shown, never offered as "Use this" — a guessed address on the contact
// would reach SuperSend and the dialer.
const emailGuessOf = (run) => (run && run.estimate && run.estimate.kind === 'email_pattern' && run.estimate.email ? run.estimate : null);
const EmailGuess = ({ g }) => (
  <div style={{ marginTop: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', wordBreak: 'break-all' }}>{g.email}</span>
      <Pill hue={g.on_file ? 'slate' : g.status === 'invalid' ? 'rose' : 'amber'} sz="sm"
        title={g.on_file ? 'Another Atlas contact with this name has this address' : g.status === 'invalid' ? 'The domain has no mail server' : 'Built from the pattern, not checked'}>
        {g.on_file ? 'In Atlas' : g.status === 'invalid' ? 'No mail server' : 'Guess'}
      </Pill>
    </div>
    <div style={{ fontSize: 11.5, marginTop: 2, color: 'var(--text-3)', wordBreak: 'break-word' }}>
      {g.basis}{(g.examples || []).length ? ` · ${g.examples.map((x) => `${x.name} → ${x.email.split('@')[0]}@`).join(', ')}` : ''}
    </div>
  </div>
);

// The Use button's words, shared by the card and the run drawer.
const researchUseLabel = (run, kind, current) => {
  const est = run.estimate && run.estimate.status === 'estimated' ? run.estimate : null;
  const useLabel = est && est.confidence !== 'high' ? `base ${revMoney(run.value)}` : run.display_value;
  if (kind === 'longtext') return current ? 'Replace with this' : 'Use this';
  return current ? `Replace with ${useLabel}` : est && est.confidence !== 'high' ? `Use ${useLabel}` : 'Use this';
};

// Record ids compare case-blind: the stores are keyed by API ids, a pasted
// link may spell the URL's id in upper case (core.normId lowercases).
const researchRid = (id) => {
  if (id == null || id === '') return '';
  const core = window.RecordPanelsCore;
  return core && core.normId ? core.normId(id) : String(id).toLowerCase();
};
// The attached store per record, so the run drawer can apply a run through the
// field that owns it (the field's own onSave) and pulse its wand.
const _storeRegistry = new Map();          // `${objectType}:${researchRid(recordId)}` → store
let _appliersQueued = false;
const _notifyAppliers = () => {            // one event per tick: fields remount on parent renders
  if (_appliersQueued) return;
  _appliersQueued = true;
  Promise.resolve().then(() => {
    _appliersQueued = false;
    try { window.dispatchEvent(new Event('atlas-research-appliers')); } catch (_) {}
  });
};
const researchStoreFor = (objectType, recordId) =>
  (objectType && recordId ? _storeRegistry.get(`${objectType}:${researchRid(recordId)}`) || null : null);
// Run ids the run drawer is showing: their finish doesn't also toast. Module
// level, so it holds whichever store attaches first (iPad panes mount after
// the drawer). Counted, so one unwatch never drops another's watch.
const _watchedRuns = new Map();           // run id → watchers
const watchResearchRun = (id) => {
  const k = String(id);
  _watchedRuns.set(k, (_watchedRuns.get(k) || 0) + 1);
  return () => {
    const n = (_watchedRuns.get(k) || 0) - 1;
    if (n > 0) _watchedRuns.set(k, n); else _watchedRuns.delete(k);
  };
};

// One store per record page. The panel only holds the handle; each wand
// subscribes to it, so a poll re-renders the wands and never the panel —
// the detail pages declare their Row component inside render, so a panel
// re-render every 1.5 s would remount every field, an open editor included.
const createResearchStore = (objectType, recordId) => {
  let state = { runs: {}, open: null };   // runs: field → { latest, applied }; open: { field, x, y, view, showSteps }
  const subs = new Set();
  let timer = null;
  let live = false;
  const key = `${objectType}:${researchRid(recordId)}`;
  const appliers = new Map();             // field → ref kept current by its ResearchField

  const runningIds = () => Object.values(state.runs)
    .map((sl) => sl && sl.latest).filter((r) => r && r.status === 'running').map((r) => r.id);
  // Poll every run in flight. A tick, not a chain of timeouts: a failed
  // fetch must still schedule the next one (the find-buyers drawer froze
  // on exactly that, 2026-09-23).
  const syncPolling = () => {
    const want = live && runningIds().length > 0;
    if (want && !timer) {
      timer = setInterval(() => {
        runningIds().forEach((id) => window.AtlasApi.getResearch(id).then(put).catch(() => {}));
      }, 1500);
    } else if (!want && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
  const set = (fn) => {
    state = { ...state, ...fn(state) };
    subs.forEach((f) => f());
    syncPolling();
  };
  const put = (run) => {
    if (!live || !run || run.record_id !== recordId) return;
    set((st) => {
      const cur = st.runs[run.field_key] || {};
      const was = cur.latest;
      // A run that finished while its card (or the run drawer) was closed says so once.
      if (was && was.id === run.id && was.status === 'running' && run.status !== 'running'
          && (!st.open || st.open.field !== run.field_key) && !_watchedRuns.has(String(run.id))) {
        pushToast({
          type: run.status === 'done' ? 'success' : 'info',
          title: `${run.label}: ${run.status === 'done' ? researchClip(run.display_value, 60) : 'nothing reliable found'}`,
          body: run.status === 'done' ? 'Click the wand to review it.' : (run.error || undefined),
        });
      }
      return { runs: { ...st.runs, [run.field_key]: { latest: run, applied: run.applied_at ? run : cur.applied } } };
    });
  };
  const anchor = (e) => ({ x: e.clientX, y: e.clientY });

  const store = {
    get enabled() { return !!(objectType && recordId) && !window.AtlasIsGuest; },
    // Use this / Dismiss / Research again: the same rule as the run drawer
    // (PanelHost's canWrite); the POSTs behind them need edit_records.
    get canWrite() {
      return !!(objectType && recordId) && !window.AtlasIsGuest
        && (typeof window.recordCanWrite === 'function' ? !!window.recordCanWrite() : true);
    },
    subscribe: (f) => { subs.add(f); return () => subs.delete(f); },
    getState: () => state,
    attach: () => {
      live = true;
      if (!objectType || !recordId) return;
      _storeRegistry.set(key, store);
      _notifyAppliers();
      window.AtlasApi.researchForRecord(objectType, recordId)
        .then((r) => { if (live) set(() => ({ runs: (r && r.fields) || {} })); })
        .catch(() => {});
    },
    detach: () => {
      live = false;
      if (_storeRegistry.get(key) === store) { _storeRegistry.delete(key); _notifyAppliers(); }
      syncPolling();
    },
    put,
    watch: watchResearchRun,
    // ref.current = { apply, value, kind, display, label }, kept current by ResearchField.
    setApplier: (field, ref) => {
      appliers.set(field, ref);
      _notifyAppliers();
      return () => { if (appliers.get(field) === ref) { appliers.delete(field); _notifyAppliers(); } };
    },
    applierFor: (field) => {
      const r = appliers.get(field);
      return r && r.current && typeof r.current.apply === 'function' ? r.current : null;
    },
    start: async (field, e) => {
      set(() => ({ open: { field, ...anchor(e), view: 'latest', showSteps: true } }));
      try {
        put(await window.AtlasApi.startResearch({ object_type: objectType, record_id: recordId, field_key: field }));
      } catch (err) {
        pushToast({ type: 'error', title: 'Research could not start', body: err.message });
        set(() => ({ open: null }));
      }
    },
    apply: async (run, onApply) => {
      try {
        await onApply(run.value);
      } catch (err) {
        pushToast({ type: 'error', title: `Couldn't save ${run.label}`, body: err.message });
        return;
      }
      try { put(await window.AtlasApi.markResearchApplied(run.id)); } catch (_) { /* the value saved; the badge can wait */ }
      set(() => ({ open: null }));
      pushToast({ type: 'success', title: `${run.label} updated`, body: researchClip(run.display_value) });
    },
    dismiss: async (run) => {
      try { put(await window.AtlasApi.dismissResearch(run.id)); } catch (_) { /* nothing to undo */ }
      set(() => ({ open: null }));
    },
    openCard: (field, e, view) => set(() => ({ open: { field, ...anchor(e), view, showSteps: view !== 'applied' } })),
    close: () => set(() => ({ open: null })),
    toggleSteps: () => set((st) => ({ open: st.open ? { ...st.open, showSteps: !st.open.showSteps } : null })),
    // The drawer refused the card's "How it got here": the card shows the steps itself.
    inlineSteps: () => set((st) => ({ open: st.open ? { ...st.open, inline: true, showSteps: true } : null })),
  };
  return store;
};

const useFieldResearch = (objectType, recordId) => {
  const store = React.useMemo(() => createResearchStore(objectType, recordId), [objectType, recordId]);
  React.useEffect(() => { store.attach(); return () => store.detach(); }, [store]);
  return store;
};

const _NO_STORE_STATE = { runs: {}, open: null };
const _NO_STORE = { subscribe: () => () => {}, getState: () => _NO_STORE_STATE };

// One steps renderer for every "How it got here": research runs, the
// ownership check's trail, hold signals (window.ResearchSteps), Prospecting.
// The icon map is the union of the research and Prospecting vocabularies.
const RUN_STEP_ICON = { think: 'Sparkle', notes: 'Mail', search: 'Search', read: 'Globe', apollo: 'Users',
  people: 'Users', verdict: 'Check', atlas: 'Database', user: 'User', error: 'Close' };

// `plain`: think rows read as verdict lines (Prospecting), not the model's asides.
const RunSteps = ({ steps, live = false, gap = 5, plain = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, marginTop: 4 }}>
    {(steps || []).map((s, i) => {
      const Ico = Icon[RUN_STEP_ICON[s.kind]] || Icon.Sparkle;
      const last = live && i === steps.length - 1;
      const aside = s.kind === 'think' && !plain;
      const links = s.links || [];
      return (
        <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start',
          color: s.kind === 'error' ? 'var(--rose-fg, #c33)' : aside ? 'var(--text-3)' : 'var(--text-2)' }}>
          <Ico size={11} style={{ flexShrink: 0, marginTop: 3, opacity: last ? 1 : 0.7 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontStyle: aside ? 'italic' : 'normal', wordBreak: 'break-word' }}>{s.label}</div>
            {s.detail && <div style={{ color: 'var(--text-4)', fontSize: 11, wordBreak: 'break-word' }}>{s.detail}</div>}
            {links.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: 2 }}>
                {links.map((l, j) => (
                  <a key={j} href={l.url} target="_blank" rel="noopener noreferrer" title={l.url}
                    onClick={(e) => e.stopPropagation()}
                    style={{ fontSize: 11, color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.title || l.url}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// Quoted, linked sources. `tag` overrides the kind's label (the ownership
// check's "ownership" / "ultimate parent", an industry page's "via").
const RunSources = ({ sources }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {(sources || []).map((s, i) => {
      let host = '';
      try { host = s.url ? new URL(s.url).hostname.replace(/^www\./, '') : ''; } catch (_) { host = ''; }
      const name = s.title || host || s.url;
      const head = s.url
        ? <a href={s.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
            style={{ color: 'var(--accent-text)', fontWeight: 500 }}>{name}</a>
        : <span style={{ fontWeight: 500, color: 'var(--text)' }}>{name}</span>;
      const tag = s.tag || ({ web: host || 'web', note: 'Atlas note', apollo: 'Apollo', atlas: 'Atlas record' }[s.kind] || s.kind);
      return (
        <div key={i} style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{head}</span>
            <span style={{ color: 'var(--text-4)', fontSize: 10.5, flexShrink: 0 }}>{tag}</span>
          </div>
          {s.quote && (
            <div style={{ color: 'var(--text-3)', fontStyle: 'italic', marginTop: 1, wordBreak: 'break-word' }}>
              “{s.quote}”
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const RunHeading = ({ children }) => (
  <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
    color: 'var(--text-4)', margin: '14px 0 6px' }}>{children}</div>
);

// The applied badge after a value research wrote: the wand's ✨ and a legacy
// AI fill's (company_detail.jsx AiFillBadge) are one mark, opening the drawer.
// Phone: 10px more from the value before it, so this 44px box stops short of the EditableCell's pencil.
const ResearchSparkle = ({ onClick, title, label }) => (
  <button type="button" onClick={onClick} title={title} aria-label={label || title}
    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0,
      display: 'inline-flex', color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null),
      ...(window.AtlasDevice && window.AtlasDevice.phone ? { marginLeft: 10 } : null) }}>
    {window.PhoneHit && <window.PhoneHit />}
    <Icon.Sparkle size={12} />
  </button>
);

// Open a run in the record page's research drawer (?panel=research). False
// when there is no drawer here (a guest, off a record page, no host on this
// surface); the caller then keeps its own UI. A click made while a Back is
// still landing (a pop-out closed a moment ago) is deferred, not dropped: it
// opens once the Back lands, on the same record only, and gives up after a
// second (record_panels.jsx refuses writes until then).
let _deferredOpen = null;
const _openResearchNow = (params) => {
  try { return !!window.openRecordPanel('research', params, { via: 'button' }); } catch (_) { return false; }
};
const openResearchRun = (params) => {
  if (window.AtlasIsGuest || typeof window.openRecordPanel !== 'function') return false;
  if (_deferredOpen) { clearTimeout(_deferredOpen); _deferredOpen = null; }   // the newest click wins
  if (_openResearchNow(params)) return true;
  const pending = typeof window.recordBackPending === 'function' && !!window.recordBackPending();
  const core = window.RecordPanelsCore;
  const at = pending && core ? core.parseRecordHash(window.location.hash) : null;
  if (!at) return false;
  let tries = 0;
  const poll = () => {
    _deferredOpen = null;
    if (window.recordBackPending()) {
      if (++tries < 20) _deferredOpen = setTimeout(poll, 50);                 // ≤ 1 s; the Back's own fallback is 800 ms
      return;
    }
    const now = core.parseRecordHash(window.location.hash);
    if (now && core.sameRecord(now, at)) _openResearchNow(params);
  };
  _deferredOpen = setTimeout(poll, 50);
  return true;
};
// Whether a click can open the drawer on this surface (decides how a pill renders).
const researchDrawerHere = () => {
  if (window.AtlasIsGuest || typeof window.recordPanelAvailable !== 'function' || typeof window.recordSurface !== 'function') return false;
  try { return !!window.recordPanelAvailable('research', window.recordSurface()); } catch (_) { return false; }
};

const RUN_STEPS_TOGGLE = { border: 'none', background: 'transparent', padding: 0,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, margin: '10px 0 0',
  fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-4)' };

const ResearchCard = ({ ctl, o, run, label, kind, value, display, onApply }) => {
  const running = run && run.status === 'running';
  useEscapeKey(true, ctl.close);
  if (!run) return null;
  const steps = run.steps || [];
  const sources = run.sources || [];
  const same = researchSame(kind, run.value, value);
  // `display` = how the page shows the current value when the stored one
  // isn't readable (an industry id → its name).
  const current = display != null && display !== '' ? display : researchShow(kind, value);
  const long = kind === 'longtext';
  const conf = RESEARCH_CONF[run.confidence];
  // A triangulated revenue (no stated figure found): the value is the base, the headline the range.
  const est = run.estimate && run.estimate.status === 'estimated' ? run.estimate : null;
  const applied = o.view === 'applied' || !!run.applied_at;
  // The whole run opens in the record page's research drawer when there is
  // one; once the drawer refused it (o.inline), the card shows the steps.
  const drawer = !o.inline && researchDrawerHere();
  const width = long ? 460 : 380;
  const top = Math.max(12, Math.min(o.y + 12, window.innerHeight - 460));
  const heading = (t) => (
    <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
      color: 'var(--text-4)', margin: '10px 0 4px' }}>{t}</div>
  );

  return ReactDOM.createPortal(
    <>
      <div onClick={(e) => { e.stopPropagation(); ctl.close(); }} style={{ position: 'fixed', inset: 0, zIndex: 400 }} />
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Research ${label}`} style={{
        position: 'fixed', left: Math.max(12, Math.min(o.x - 20, window.innerWidth - width - 12)), top, zIndex: 401,
        width, maxHeight: Math.min(560, window.innerHeight - top - 12), overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10,
        boxShadow: 'var(--shadow-md, var(--shadow-sm))', padding: '10px 12px 12px',
        fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <Icon.Wand size={13} color="var(--accent-text)" />
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>Research · {label}</span>
          <span style={{ flex: 1 }} />
          <button onClick={ctl.close} title="Close" style={{ border: 'none', background: 'transparent',
            cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}>
            <Icon.Close size={12} />
          </button>
        </div>

        {running && steps.length > 0 && <RunSteps steps={steps} live />}

        {run.status === 'done' && !long && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', wordBreak: 'break-all' }}>
                {run.display_value}
              </span>
              {conf && <Pill hue={conf.hue} sz="sm">{conf.label}</Pill>}
            </div>
            {est && <RevenueRange est={est} />}
            <div style={{ fontSize: 11.5, marginTop: 2, color: same ? 'var(--green-fg, var(--text-3))' : 'var(--text-3)' }}>
              {same ? 'Matches what Atlas has.' : current ? `Atlas has ${current}.` : 'Atlas has nothing for this field yet.'}
            </div>
          </>
        )}
        {run.status === 'done' && long && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {conf && <Pill hue={conf.hue} sz="sm">{conf.label}</Pill>}
              <span style={{ fontSize: 11.5, color: same ? 'var(--green-fg, var(--text-3))' : 'var(--text-3)' }}>
                {same ? 'Same as what Atlas has.' : current ? 'Replaces the text Atlas has now.' : 'The field is empty in Atlas.'}
              </span>
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto', padding: '8px 10px', borderRadius: 6,
              background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.55 }}>
              {run.display_value}
            </div>
          </>
        )}

        {run.status === 'not_found' && (
          <div style={{ fontWeight: 500, color: 'var(--text)' }}>Couldn't find a reliable value for {label}.</div>
        )}
        {run.status === 'not_found' && emailGuessOf(run) && <EmailGuess g={emailGuessOf(run)} />}
        {run.status === 'failed' && (
          <div style={{ color: 'var(--rose-fg, var(--text))' }}>The research failed: {run.error || 'unknown error'}</div>
        )}

        {!running && run.reasoning && (
          <>
            {long && run.status === 'done' && heading('How it was written')}
            <div style={{ marginTop: long && run.status === 'done' ? 0 : 8, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{run.reasoning}</div>
            {est && run.status === 'done' && <RevenueMethods est={est} />}
          </>
        )}

        {!running && sources.length > 0 && (
          <>
            {heading('Sources')}
            <RunSources sources={sources} />
          </>
        )}

        {!running && steps.length > 0 && (drawer ? (
          // Open the drawer first, then close the card: a refused open keeps the card.
          <button onClick={() => { if (openResearchRun({ src: 'field', run: run.id })) ctl.close(); else ctl.inlineSteps(); }}
            style={RUN_STEPS_TOGGLE}>
            How it got here · {steps.length} step{steps.length === 1 ? '' : 's'}
            <Icon.ChevronR size={10} />
          </button>
        ) : (
          <>
            <button onClick={ctl.toggleSteps} style={RUN_STEPS_TOGGLE}>
              {o.showSteps ? <Icon.ChevronD size={10} /> : <Icon.ChevronR size={10} />}
              How it got here · {steps.length} step{steps.length === 1 ? '' : 's'}
            </button>
            {o.showSteps && <RunSteps steps={steps} />}
          </>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {run.status === 'done' && !applied && !same && ctl.canWrite && (
            <Btn variant="primary" size="sm" onClick={() => ctl.apply(run, onApply)}>
              {researchUseLabel(run, kind, current)}
            </Btn>
          )}
          {run.status === 'done' && !applied && !run.dismissed_at && ctl.canWrite && (
            <Btn variant="ghost" size="sm" onClick={() => ctl.dismiss(run)}>Dismiss</Btn>
          )}
          {!running && ctl.canWrite && (
            <Btn variant={run.status === 'done' && !applied ? 'ghost' : 'outline'} size="sm"
              onClick={() => ctl.start(run.field_key, { clientX: o.x, clientY: o.y })}>
              {run.status === 'done' ? 'Research again' : 'Try again'}
            </Btn>
          )}
        </div>
        {!running && (
          <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--text-4)' }}>
            {applied && run.applied_at
              ? `Used ${fmtDate(run.applied_at)}${run.applied_by ? ` by ${run.applied_by}` : ''}`
              : `Researched ${relativeTime(run.created_at)}${run.requested_by ? ` by ${run.requested_by}` : ''}`}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};

// `write` = the field is drafted (buyer-marketing text), not looked up.
const ResearchField = ({ ctl, field, label, kind = 'text', value, display, onApply, write = false, legacy = null }) => {
  const [hover, setHover] = React.useState(false);
  const st = React.useSyncExternalStore((ctl || _NO_STORE).subscribe, (ctl || _NO_STORE).getState);
  // The run drawer's Use this saves through this field (its onApply), never a
  // copy. A ref, so the inline onApply arrow doesn't re-register every render.
  const applierRef = React.useRef(null);
  applierRef.current = onApply ? { apply: onApply, value, kind, display, label } : null;
  React.useEffect(() => (ctl && ctl.setApplier && field ? ctl.setApplier(field, applierRef) : undefined), [ctl, field]);
  if (!ctl) return legacy;
  const slot = st.runs[field] || {};
  const latest = slot.latest;
  const applied = slot.applied;
  const showBadge = applied && researchSame(kind, applied.value, value);
  const running = latest && latest.status === 'running';
  const pending = latest && latest.status === 'done' && !latest.applied_at && !latest.dismissed_at
    && !researchSame(kind, latest.value, value);
  // A finding from the last day reopens instead of paying for a new run;
  // the card's "Research again" is one click away.
  const recent = latest && latest.status !== 'failed' && !latest.dismissed_at && !latest.applied_at
    && Date.now() - new Date(latest.created_at).getTime() < 24 * 3600 * 1000;
  const o = st.open;
  const isOpen = o && o.field === field;
  const cardRun = isOpen ? (o.view === 'applied' ? applied : latest) : null;
  const tip = running ? `${write ? 'Writing' : 'Researching'} ${label}… click to watch`
    : pending ? `${write ? 'A draft is ready' : `Research found ${researchClip(latest.display_value, 60)}`} — click to review`
    : write ? `Write ${label} with AI`
    : `Research ${label} with AI`;

  return (
    <>
      {showBadge ? (
        <ResearchSparkle
          onClick={(e) => { e.stopPropagation(); if (!openResearchRun({ src: 'field', run: applied.id })) ctl.openCard(field, e, 'applied'); }}
          title="Found by research — click for the sources and how it got here" />
      ) : legacy}
      {ctl.enabled && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (running || pending || recent) ctl.openCard(field, e, 'latest');
            else ctl.start(field, e);
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          title={tip}
          aria-label={tip}
          style={{
            position: 'relative', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 18, height: 18, borderRadius: 4, padding: 0, cursor: 'pointer',
            border: '1px solid ' + (hover || running || pending ? 'var(--border)' : 'transparent'),
            background: hover ? 'var(--surface-hover)' : 'transparent',
            color: running || pending ? 'var(--accent-text)' : hover ? 'var(--text-2)' : 'var(--text-4)',
            animation: running ? 'atlasResearchPulse 1.2s ease-in-out infinite' : 'none',
            // Phone: 10px more from the sparkle (or the pencil) before it, so this 44px box stops short of that glyph.
            ...(window.AtlasDevice && window.AtlasDevice.phone ? { marginLeft: 10 } : null),
          }}>
          {window.PhoneHit && <window.PhoneHit />}
          <Icon.Wand size={11} />
          {pending && (
            <span style={{ position: 'absolute', top: -2, right: -2, width: 6, height: 6, borderRadius: 999,
              background: 'var(--accent)' }} />
          )}
        </button>
      )}
      {isOpen && cardRun && (
        <ResearchCard ctl={ctl} o={o} run={cardRun} label={label} kind={kind} value={value} display={display} onApply={onApply} />
      )}
    </>
  );
};

// ── The run drawer (?panel=research&src=…): one adapter per source ──────────
// Each adapter loads a RunView — { status, label, value, valueKind,
// confidence, reasoning, sources, steps, at, by, verb, error, extra,
// actions } — that ResearchRunView renders the same way for every source.
// The pure mappers (RESEARCH_VIEWS) are node-tested apart from the fetching.
const RUN_STATUSES = ['running', 'done', 'not_found', 'failed'];
const runNotFound = (label) => ({ status: 'not_found', label: label || null, value: null, valueKind: 'text',
  confidence: null, reasoning: null, sources: [], steps: [], at: null, by: null, verb: null, error: null, extra: null, actions: {} });
const isNotFound = (e) => /^404\b/.test(String((e && e.message) || ''));   // apiFetch errors read "404 Not Found: …"
const runErrorView = (e) => (isNotFound(e) ? runNotFound(null)
  : { ...runNotFound(null), status: 'failed', error: researchClip(String((e && e.message) || e), 200) });
const runHostPath = (u) => {
  try { const x = new URL(u); return x.hostname.replace(/^www\./, '') + (x.pathname && x.pathname !== '/' ? x.pathname.replace(/\/$/, '') : ''); }
  catch (_) { return u || ''; }
};
// The page's own section re-reads (OwnershipStatus, Facts & fit) after the drawer re-runs it.
const runChanged = (src, companyId) => {
  try { window.dispatchEvent(new CustomEvent('atlas-research-changed', { detail: { src, companyId } })); } catch (_) {}
};
const runPages = (n) => `${n} page${n === 1 ? '' : 's'}`;

// field: a research run (GET /api/research/{run}).
const fieldView = (run, ctx) => {
  if (!run || !run.id) return runNotFound(null);
  // Record isolation: a run id on another record's URL shows nothing (ids case-blind).
  if (ctx && ctx.objectType && (run.object_type !== ctx.objectType || researchRid(run.record_id) !== researchRid(ctx.recordId))) return runNotFound(null);
  const est = run.estimate && run.estimate.status === 'estimated' ? run.estimate : null;
  const applied = !!run.applied_at;
  const dismissed = !applied && !!run.dismissed_at;
  return {
    status: RUN_STATUSES.includes(run.status) ? run.status : 'failed',
    label: run.label || null,
    value: run.status === 'done' ? (run.display_value || null) : null,
    valueKind: run.kind === 'longtext' ? 'longtext' : run.kind === 'money' ? 'money' : 'text',
    confidence: RESEARCH_CONF[run.confidence] ? run.confidence : null,
    reasoning: run.reasoning || null, sources: run.sources || [], steps: run.steps || [],
    guess: run.status === 'not_found' ? emailGuessOf(run) : null,
    at: applied ? run.applied_at : dismissed ? run.dismissed_at : (run.created_at || null),
    by: applied ? (run.applied_by || null) : dismissed ? null : (run.requested_by || null),
    verb: applied ? 'Used' : dismissed ? 'Dismissed' : 'Researched',
    error: run.status === 'failed' ? (run.error || null) : null,
    extra: est && run.status === 'done' ? <><RevenueRange est={est} /><RevenueMethods est={est} /></> : null,
    actions: {},
  };
};
// Use this / Dismiss / Research again for a field run on this record. Use
// saves through the field's own onApply (its ResearchField registered it), so
// it is offered only while that field is on the page and the viewer can write.
const fieldExtras = (run, ctx) => {
  // Looked up at call time: the page's store can attach after the run loads.
  const storeNow = () => researchStoreFor(run.object_type, run.record_id);
  const applier = () => { const st = storeNow(); return st ? st.applierFor(run.field_key) : null; };
  const compare = () => {
    const a = applier();
    if (!a) return null;
    const k = a.kind || run.kind;
    return { same: researchSame(k, run.value, a.value),
             current: a.display != null && a.display !== '' ? a.display : researchShow(k, a.value) };
  };
  const actions = {};
  if (ctx.canWrite) {
    if (run.status === 'done' && !run.applied_at) {
      actions.use = async () => { const st = storeNow(); const a = applier(); if (!a || !st) throw new Error('No field to save to'); await st.apply(run, a.apply); };
      // At render: a field can mount after the run loads (iPad panes mount after measuring).
      actions.useLabel = () => { const a = applier(); const c = compare(); return !a || !c || c.same ? null : researchUseLabel(run, a.kind || run.kind, c.current); };
      if (!run.dismissed_at) actions.dismiss = async () => { const st = storeNow(); if (st) await st.dismiss(run); else await window.AtlasApi.dismissResearch(run.id); };
    }
    if (run.status !== 'running') {
      actions.again = async () => {
        const fresh = await window.AtlasApi.startResearch({ object_type: run.object_type, record_id: run.record_id, field_key: run.field_key });
        const st = storeNow();
        if (st && fresh) st.put(fresh);          // the wand pulses while it runs
        return fresh && fresh.id ? { run: fresh.id } : null;
      };
      actions.againLabel = run.status === 'done' ? 'Research again' : 'Try again';
    }
  }
  return { compare, actions };
};

// fill: a legacy AI fill (company enrichment_data.fills[key], AiFillBadge's data).
const FILL_LABELS = { aka: 'Also known as', website: 'Website', hq_address: 'HQ address', revenue_est: 'Revenue', ebitda_est: 'EBITDA' };
const FILL_MONEY = { revenue_est: true, ebitda_est: true };
const FILL_VIA = { batch: 'Bulk fill', firecrawl: 'Firecrawl', geo_enrichment: 'Geo enrichment' };
const humanizeKey = (k) => { const s = String(k || '').replace(/_/g, ' ').trim(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Field'; };
const fillLabel = (key) => FILL_LABELS[key] || humanizeKey(key);
const fillsOf = (rec) => ((((rec || {})._raw || {}).enrichment_data || {}).fills) || {};
const fillView = (fill, key) => {
  const label = fillLabel(key);
  if (!fill) return runNotFound(label);
  const raw = fill.value == null ? '' : String(fill.value);
  const money = !!FILL_MONEY[key] && raw !== '' && Number.isFinite(Number(raw));
  const value = raw === '' ? null : money ? fmtMoney(Number(raw)) : raw;
  const sources = (fill.sources || []).filter((s) => s && s.url).map((s) => ({ kind: 'web', url: s.url, title: s.title || runHostPath(s.url) }));
  const answer = fill.raw != null ? String(fill.raw).trim() : '';
  const web = fill.fill_mode === 'web_search';
  return {
    status: 'done', label, value, valueKind: money ? 'money' : 'text', confidence: null,
    reasoning: answer && answer !== raw.trim() ? answer : null,      // the model's own answer, when it says more than the value
    sources,
    steps: [
      { kind: web ? 'search' : 'think', label: web ? 'Searched the web' : 'Asked the model', detail: fill.model || null },
      sources.length ? { kind: 'read', label: `Cited ${runPages(sources.length)}`, links: sources.map((s) => ({ url: s.url, title: s.title })) } : null,
      { kind: 'verdict', label: `Wrote ${label}`, detail: value },
    ].filter(Boolean),
    at: fill.filled_at || null, by: FILL_VIA[fill.via] || null, verb: 'Filled', error: null, extra: null, actions: {},
  };
};

// ownership: the ownership check (GET /api/companies/{id}/ownership) and its trail.
const OwnershipFacts = ({ own }) => {
  const bought = own.acquired_by || own.ultimate_parent;
  if (!bought && !own.manual_outcome) return null;
  return (
    <div style={{ marginTop: 4, color: 'var(--text-2)' }}>
      {bought && (
        <div>
          {own.acquired_by && <>Acquired by <b>{own.acquired_by}</b></>}
          {own.ultimate_parent && own.ultimate_parent !== own.acquired_by
            && <>{own.acquired_by ? ' · ' : ''}ultimate parent <b>{own.ultimate_parent}</b></>}
          {own.sponsor_buyer_id && <> · <button type="button" onClick={() => window.AtlasNav && window.AtlasNav.goToBuyer && window.AtlasNav.goToBuyer(own.sponsor_buyer_id)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--accent-text)' }}>on our buyer roster</button></>}
        </div>
      )}
      {own.manual_outcome && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--text-3)' }}>
          Set by hand to {own.outcome_label}{own.reason_label ? ` · ${own.reason_label}` : ''}{own.edited_by_name ? ` by ${own.edited_by_name}` : ''}{own.manual_note ? ` · ${own.manual_note}` : ''}
        </div>
      )}
    </div>
  );
};
const ownershipView = (own) => {
  if (!own || own.exists === false || !own.status) return runNotFound('Ownership');
  const status = ['queued', 'running'].includes(own.status) ? 'running' : own.status === 'failed' ? 'failed' : 'done';
  const conf = own.ultimate_parent ? (own.step2_confidence || own.confidence) : own.confidence;
  const sources = [
    { tag: 'ownership', url: own.evidence_url, quote: own.evidence_text },
    { tag: 'ultimate parent', url: own.step2_evidence_url, quote: own.step2_evidence_text },
  ].filter((x) => x.url || x.quote).map((x) => ({
    kind: x.url ? 'web' : 'atlas', url: x.url || undefined,
    title: x.url ? runHostPath(x.url) : (own.source === 'clay' ? 'Clay' : 'No page'), quote: x.quote || null, tag: x.tag,
  }));
  return {
    status, label: 'Ownership', value: status === 'done' ? (own.final_label || null) : null, valueKind: 'verdict',
    confidence: RESEARCH_CONF[conf] ? conf : null,
    reasoning: [own.reasoning, own.step2_reasoning ? `Ultimate parent: ${own.step2_reasoning}` : null].filter(Boolean).join('\n\n') || null,
    sources, steps: own.steps || [],
    at: own.checked_at || null, by: own.source === 'clay' ? 'Clay' : null, verb: 'Checked',
    error: status === 'failed' ? (own.error || null) : null,
    extra: status === 'done' ? <OwnershipFacts own={own} /> : null, actions: {},
  };
};

// tier: the size tier and fit (GET /api/company-facts/{id} → qualification, #718 signals).
const tierView = (facts) => {
  const q = facts && facts.qualification;
  if (!q) return runNotFound('Size tier');
  const T = window.COMPANY_TIER || { label: {}, hue: {} };
  const d = q.size_detail || {};
  const o = facts.observables || {};
  const ident = ((q.evidence || {}).identity) || {};
  const urls = (ident.page_urls || []).filter(Boolean);
  const tier = T.label[q.size_tier] || q.size_tier || null;
  const sources = [
    ...urls.map((u) => ({ kind: 'web', url: u, title: runHostPath(u) })),
    o.linkedin_url && o.linkedin_headcount ? { kind: 'web', url: o.linkedin_url, title: 'LinkedIn', quote: `${Number(o.linkedin_headcount).toLocaleString()} employees` } : null,
    o.google_maps_url && o.google_rating != null ? { kind: 'web', url: o.google_maps_url, title: 'Google', quote: `${o.google_rating}★ · ${Number(o.google_reviews || 0).toLocaleString()} reviews` } : null,
  ].filter(Boolean);
  const pct = q.industry_confidence != null ? ` · ${Math.round(q.industry_confidence * 100)}% match` : '';
  const from = { seller_profile: 'the seller brief', company_record: 'the Atlas company record' }[ident.from] || ident.from;
  const steps = [
    urls.length ? { kind: 'read', label: `Read ${runPages(urls.length)} of the website`, links: urls.map((u) => ({ url: u, title: runHostPath(u) })) }
      : ident.from ? { kind: 'read', label: `Used ${from}` } : null,
    { kind: 'think', label: `Industry: ${q.industry_name || q.industry_proposed || 'none fits'}${pct}`, detail: q.industry_reason || null },
    q.geo_fit ? { kind: 'think', label: `Geography: ${q.geo_fit}`, detail: q.geo_reason || null } : null,
    d.disqualifier ? { kind: 'error', label: d.disqualifier } : null,
    { kind: 'verdict', label: `Size: ${tier || '—'}`, detail: [d.evidence_source, d.data_completeness ? `${d.data_completeness} data` : null].filter(Boolean).join(' · ') || null },
  ].filter(Boolean);
  const checks = (d.signals || []).length || (q.size_reasons || []).length;
  return {
    status: 'done', label: 'Size tier', value: tier, valueKind: 'verdict',
    confidence: RESEARCH_CONF[d.confidence] ? d.confidence : null,
    reasoning: d.reasoning || q.qualification_reason || null, sources, steps,
    at: q.qualified_at || null, by: q.source === 'prospecting' ? 'Prospecting' : null, verb: 'Qualified', error: null,
    extra: checks && window.SizeChecks ? <window.SizeChecks detail={q.size_detail} reasons={q.size_reasons} hideReasoning /> : null,
    actions: {},
  };
};

// industry: an industry suggestion (GET /api/cleanup/industry-proposals/{run}/evidence).
const INDUSTRY_FLAG_FALLBACK = { seller_profile: 'From the seller brief' };
const industryView = (ev, ctx) => {
  if (!ev || !ev.id) return runNotFound('Industry');
  if (ev.company_id && ctx && ctx.companyId && researchRid(ev.company_id) !== researchRid(ctx.companyId)) return runNotFound('Industry');
  const flags = window.INDUSTRY_EVIDENCE_FLAG || {};
  const read = (ev.pages || []).filter((pg) => pg && pg.url);
  const rc = ev.review_confidence != null ? ev.review_confidence : ev.confidence;
  const site = ev.website ? revHost(ev.website.startsWith('http') ? ev.website : `https://${ev.website}`) : '';
  return {
    status: 'done', label: 'Industry', value: ev.suggested_industry || 'No industry fits', valueKind: 'verdict',
    confidence: rc == null ? null : rc >= 0.85 ? 'high' : rc >= 0.6 ? 'medium' : 'low',     // Cleanup's pill thresholds
    reasoning: ev.evidence || null,
    sources: read.map((pg) => ({ kind: 'web', url: pg.url, title: runHostPath(pg.url),
      quote: pg.excerpt ? researchClip(String(pg.excerpt).trim(), 600) : null, tag: pg.via || undefined })),
    steps: [
      read.length ? { kind: 'read', label: `Read ${runPages(read.length)}${site ? ` of ${site}` : ''}`,
        links: read.map((pg) => ({ url: pg.url, title: runHostPath(pg.url) })) } : null,
      ...(ev.evidence_flags || []).map((f) => ({ kind: 'error', label: (flags[f] && flags[f].label) || INDUSTRY_FLAG_FALLBACK[f] || f })),
      read.length && !ev.has_excerpts ? { kind: 'error', label: 'Page text not kept' } : null,
      { kind: 'think', label: ev.suggested_industry ? `Suggested ${ev.suggested_industry}` : 'No industry fits',
        detail: rc != null ? `${Math.round(rc * 100)}% sure${ev.evidence_quality != null ? ` · evidence ${Math.round(ev.evidence_quality * 100)}%` : ''}` : null },
      ev.status && ev.status !== 'pending' ? { kind: 'user',
        label: `${revCap(ev.status)}${ev.applied_industry ? ` · ${ev.applied_industry}` : ''}${ev.decided_by_name ? ` by ${ev.decided_by_name}` : ''}`,
        detail: ev.decided_at ? fmtDate(ev.decided_at) : (ev.superseded_reason || null) } : null,
    ].filter(Boolean),
    at: ev.read_at || null, by: null, verb: 'Read', error: null, extra: null, actions: {},
  };
};

const RESEARCH_VIEWS = { field: fieldView, fill: fillView, ownership: ownershipView, tier: tierView, industry: industryView };
// title(params, { label }) → the drawer title; load(params, ctx) → a RunView.
// ctx = { kind, recordId, objectType, companyId, record, facts, canWrite }.
const RESEARCH_ADAPTERS = {
  field: {
    title: (p, c) => (c && c.label ? `Research · ${c.label}` : 'Research'),
    load: async (p, ctx) => {
      const run = await window.AtlasApi.getResearch(p.run);
      const v = fieldView(run, ctx);
      const mine = run && run.id && run.object_type === ctx.objectType && researchRid(run.record_id) === researchRid(ctx.recordId);
      return mine ? { ...v, ...fieldExtras(run, ctx) } : v;
    },
  },
  fill: {
    title: (p) => fillLabel(p.field),
    load: async (p, ctx) => fillView(fillsOf(ctx.record)[p.field], p.field),
  },
  ownership: {
    title: () => 'Ownership',
    load: async (p, ctx) => {
      if (!ctx.companyId) return runNotFound('Ownership');
      const v = ownershipView(await window.AtlasApi.companyOwnership(ctx.companyId));
      // Override stays on the rail's OwnershipStatus; a buyer firm has no check.
      if (ctx.canWrite && v.status !== 'running' && !(ctx.facts && ctx.facts.buyerFirm)) {
        v.actions = {
          again: async () => { await window.AtlasApi.refreshCompanyOwnership(ctx.companyId); runChanged('ownership', ctx.companyId); return null; },
          againLabel: v.status === 'not_found' ? 'Check ownership' : 'Check again',
        };
      }
      return v;
    },
  },
  tier: {
    title: () => 'Size tier',
    load: async (p, ctx) => {
      if (!ctx.companyId) return runNotFound('Size tier');
      const v = tierView(await window.AtlasApi.companyFacts(ctx.companyId));
      if (ctx.canWrite && !(ctx.facts && ctx.facts.buyerFirm)) {
        v.actions = {
          again: async () => { await window.AtlasApi.qualifyCompany(ctx.companyId); runChanged('tier', ctx.companyId); return null; },
          againLabel: v.status === 'not_found' ? 'Qualify' : 'Re-qualify',
        };
      }
      return v;
    },
  },
  industry: {
    title: () => 'Industry',
    load: async (p, ctx) => industryView(await window.AtlasApi.industryProposalEvidence(p.run), ctx),
  },
};

// The drawer title: a field's label is only known once its run loads, so the
// body fills a small label cache the title reads.
const _runLabels = new Map();
const _labelSubs = new Set();
const _subscribeLabels = (f) => { _labelSubs.add(f); return () => { _labelSubs.delete(f); }; };
const setRunLabel = (key, label) => {
  if (!label || _runLabels.get(key) === label) return;
  _runLabels.set(key, label);
  _labelSubs.forEach((f) => f());
};
const runKey = (p) => `${p.src || ''}|${p.run || ''}|${p.field || ''}`;
// What load() reads besides the URL, so a change re-loads and the actions
// follow: whoami lands after a cold link (canWrite), the buyer roster after
// the company (buyerFirm), a contact can move company.
const runLoadGate = (ctx) => [ctx.canWrite ? 'w' : 'r', ctx.facts && ctx.facts.buyerFirm ? 'buyer' : '',
  researchRid(ctx.companyId)].join('|');
const ResearchRunTitle = ({ api }) => {
  const last = React.useRef({});
  if (api && api.params && api.params.src) last.current = api.params;   // keeps the title through the close animation
  const p = last.current;
  const key = runKey(p);
  const label = React.useSyncExternalStore(_subscribeLabels, () => _runLabels.get(key) || null, () => null);
  const ad = RESEARCH_ADAPTERS[p.src];
  return <>{ad ? ad.title(p, { label }) : 'Research'}</>;
};

// Value + confidence, reasoning, sources, How it got here, then the actions.
// Presentational (no hooks): ResearchRunBody owns the state.
const ResearchRunView = ({ view, busy, onAction }) => {
  if (!view) return <div style={{ fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>;
  const v = view;
  const running = v.status === 'running';
  const done = v.status === 'done';
  const conf = done ? RESEARCH_CONF[v.confidence] : null;
  const cmp = done && v.compare ? v.compare() : null;
  const act = v.actions || {};
  const useLbl = done && act.use ? (act.useLabel ? act.useLabel() : 'Use this') : null;
  const long = v.valueKind === 'longtext';
  const n = (v.steps || []).length;
  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {!long && (
          <span style={{ fontSize: 18, fontWeight: 600, wordBreak: 'break-word',
            color: done && v.value ? 'var(--text)' : 'var(--text-4)' }}>{done && v.value ? v.value : '—'}</span>
        )}
        {conf && <Pill hue={conf.hue} sz="sm">{conf.label}</Pill>}
        {running && <Pill hue="blue" sz="sm">Running</Pill>}
        {v.status === 'not_found' && <Pill hue="slate" sz="sm">Not found</Pill>}
        {v.status === 'failed' && <Pill hue="rose" sz="sm">Failed</Pill>}
        {cmp && cmp.same && <Pill hue="green" sz="sm">Matches Atlas</Pill>}
      </div>
      {cmp && !cmp.same && cmp.current && !long && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2, wordBreak: 'break-word' }}>Atlas · {cmp.current}</div>
      )}
      {long && done && v.value && (
        <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 6, background: 'var(--surface-2)',
          border: '1px solid var(--border)', color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v.value}</div>
      )}
      {v.error && <div style={{ marginTop: 4, color: 'var(--text-3)', wordBreak: 'break-word' }}>{v.error}</div>}
      {v.guess && <EmailGuess g={v.guess} />}
      {done && v.extra}
      {!running && v.reasoning && (<><RunHeading>Reasoning</RunHeading><div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v.reasoning}</div></>)}
      {!running && (v.sources || []).length > 0 && (<><RunHeading>Sources · {v.sources.length}</RunHeading><RunSources sources={v.sources} /></>)}
      {n > 0 && (<><RunHeading>How it got here · {n} step{n === 1 ? '' : 's'}</RunHeading><RunSteps steps={v.steps} live={running} /></>)}
      {(useLbl || act.dismiss || (act.again && !running)) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
          {useLbl && <Btn variant="primary" size="sm" disabled={!!busy} onClick={() => onAction('use')}>{useLbl}</Btn>}
          {act.dismiss && <Btn variant="ghost" size="sm" disabled={!!busy} onClick={() => onAction('dismiss')}>Dismiss</Btn>}
          {act.again && !running && (
            <Btn variant={useLbl ? 'ghost' : 'outline'} size="sm" disabled={!!busy} onClick={() => onAction('again')}>{act.againLabel || 'Research again'}</Btn>
          )}
        </div>
      )}
      {v.at && !running && (
        <div style={{ marginTop: 10, fontSize: 10.5, color: 'var(--text-4)' }}>
          {[v.verb, relativeTime(v.at)].filter(Boolean).join(' ')}{v.by ? ` · ${v.by}` : ''}
        </div>
      )}
    </div>
  );
};

// The drawer body PanelHost renders for ?panel=research (record_panels.jsx).
// Every hook, no early return. Polls while the run is running.
const ResearchRunBody = ({ api }) => {
  const a = api || {};
  // The drawer keeps rendering through its close animation; hold the last
  // params and stop loading once it is no longer the open drawer.
  const lastParams = React.useRef({});
  if (a.params && a.params.src) lastParams.current = a.params;
  const p = lastParams.current;
  const active = a.active !== false && !!(a.params && a.params.src);
  const rec = a.rec || {};
  const core = window.RecordPanelsCore || {};
  const ctx = {
    kind: rec.kind || null, recordId: rec.id || null,
    objectType: ((core.OBJECT_TYPE || {})[rec.kind]) || null,
    companyId: (a.ctx && a.ctx.companyId) || (a.facts && a.facts.companyId) || (rec.kind === 'company' ? rec.id : null) || null,
    record: a.record || null, facts: a.facts || null, canWrite: a.canWrite === true,
  };
  const ctxRef = React.useRef(ctx);
  ctxRef.current = ctx;
  const key = runKey(p);
  const gate = runLoadGate(ctx);
  const [loaded, setLoaded] = React.useState({ key: null, view: null });
  const [tick, setTick] = React.useState(0);
  const [busy, setBusy] = React.useState(null);
  const [, setApplierTick] = React.useState(0);
  const seq = React.useRef(0);

  // Load on open, on a new run, on every poll tick and when the write gate
  // changes (the actions are decided in load). Only the newest request
  // lands; a failed poll keeps what is on screen.
  React.useEffect(() => {
    const ad = RESEARCH_ADAPTERS[p.src];
    if (!ad || !active) return;
    const my = ++seq.current;
    Promise.resolve()
      .then(() => ad.load(p, ctxRef.current))
      .then((view) => { if (seq.current === my) setLoaded({ key, view }); })
      .catch((e) => { if (seq.current === my) setLoaded((s) => (s.key === key && s.view ? s : { key, view: runErrorView(e) })); });
  }, [key, tick, active, gate]);   // eslint-disable-line react-hooks/exhaustive-deps

  const view = loaded.key === key ? loaded.view : null;
  const running = !!(view && view.status === 'running');

  // A tick, not a chain of timeouts: a failed fetch still schedules the next.
  React.useEffect(() => {
    if (!running || !active) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1500);
    return () => clearInterval(t);
  }, [running, active, key]);

  React.useEffect(() => { if (view && view.label) setRunLabel(key, view.label); }, [view, key]);

  // While this run is on screen the wand's store doesn't also toast its
  // finish, whenever that store attaches.
  React.useEffect(() => (p.src === 'field' && p.run && active ? watchResearchRun(p.run) : undefined),
    [p.src, p.run, active]);

  // A field's wand mounting or leaving changes whether Use this can show.
  React.useEffect(() => {
    const f = () => setApplierTick((n) => n + 1);
    window.addEventListener('atlas-research-appliers', f);
    return () => window.removeEventListener('atlas-research-appliers', f);
  }, []);

  const onAction = async (name) => {
    const fn = view && view.actions && view.actions[name];
    if (!fn || busy) return;
    setBusy(name);
    try {
      const next = await fn();
      if (name === 'again' && next && typeof next === 'object' && a.set) a.set(next, { via: 'button' });   // replaces the entry
      else setTick((n) => n + 1);
    } catch (e) {
      pushToast({ type: 'error', title: name === 'again' ? 'Could not start it' : name === 'dismiss' ? 'Could not dismiss it' : 'Could not save it',
        body: String((e && e.message) || e) });
    } finally {
      setBusy(null);
    }
  };

  return <ResearchRunView view={view} busy={busy} onAction={onAction} />;
};

if (typeof document !== 'undefined' && !document.getElementById('atlas-research-kf')) {
  const st = document.createElement('style');
  st.id = 'atlas-research-kf';
  st.textContent = '@keyframes atlasResearchPulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } }';
  document.head.appendChild(st);
}

// RunSteps is the one steps renderer (the ownership check's trail, hold
// signals, Prospecting); ResearchSteps stays as its old name (company_signals.jsx).
Object.assign(window, { useFieldResearch, ResearchField, RunSteps, RunSources,
  RevenueEstimate: { money: revMoney, range: revRange, label: revLabel, Range: RevenueRange, Methods: RevenueMethods } });
window.ResearchSteps = RunSteps;
// The research run drawer (?panel=research) and what opens it.
Object.assign(window, { RESEARCH_ADAPTERS, RESEARCH_VIEWS, ResearchRunBody, ResearchRunView, ResearchRunTitle,
  openResearchRun, researchDrawerHere, researchStoreFor, createResearchStore, researchErrorView: runErrorView,
  ResearchCard, ResearchSparkle, watchResearchRun, researchLoadGate: runLoadGate,
  // OwnershipCard (company_detail.jsx) reads the ownership mapping from here: one copy.
  OwnershipFacts });
